import { supabase } from "@/integrations/supabase/client";

type SiteKey = 'bbq';

interface LeadData {
  email: string;
  name?: string;
  phone?: string;
  city?: string;
  zip?: string;
  sourcePage?: string;
  savingsCode?: string;
  category?: string;
  message?: string;
  projectType?: string;
  timeline?: string;
  leadType?: 'quote' | 'lead' | 'wizard' | 'ppc' | 'contact';
}

interface NotificationData {
  leadType: 'quote' | 'lead' | 'wizard' | 'ppc' | 'contact';
  name?: string;
  email: string;
  phone?: string;
  city?: string;
  zip?: string;
  category?: string;
  message?: string;
  sourcePage?: string;
  projectType?: string;
  timeline?: string;
  region?: 'texas' | 'california';
}

// Detect region from source page
const detectRegion = (sourcePage?: string): 'texas' | 'california' => {
  if (!sourcePage) return 'texas';
  const path = sourcePage.toLowerCase();
  if (path.includes('california') || path.includes('inland-empire') || path.includes('los-angeles') || path.includes('/ca/')) {
    return 'california';
  }
  return 'texas';
};

// Send notification email (fire and forget - don't block lead submission)
const sendNotification = async (data: NotificationData): Promise<void> => {
  try {
    console.log('[Leads] Sending notification for:', data.leadType);
    
    const { error } = await supabase.functions.invoke('send-lead-notification', {
      body: data,
    });
    
    if (error) {
      console.error('[Leads] Notification error:', error);
    } else {
      console.log('[Leads] Notification sent successfully');
    }
  } catch (err) {
    // Don't let notification failure affect lead submission
    console.error('[Leads] Notification failed:', err);
  }
};

export const submitLead = async (
  siteKey: SiteKey,
  data: LeadData
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase.from('leads').insert({
      site_key: siteKey,
      email: data.email.trim().toLowerCase(),
      name: data.name?.trim() || null,
      phone: data.phone?.trim() || null,
      city: data.city?.trim() || null,
      zip: data.zip?.trim() || null,
      source_page: data.sourcePage || window.location.pathname,
      savings_code: data.savingsCode || null,
      lead_type: data.leadType || 'lead',
      notes: data.message || null,
    });

    if (error) {
      console.error('[Leads] Error submitting lead:', error);
      return { success: false, error: error.message };
    }

    // Send notification (fire and forget)
    sendNotification({
      leadType: data.leadType || 'lead',
      name: data.name,
      email: data.email,
      phone: data.phone,
      city: data.city,
      zip: data.zip,
      category: data.category,
      message: data.message,
      sourcePage: data.sourcePage || window.location.pathname,
      projectType: data.projectType,
      timeline: data.timeline,
      region: detectRegion(data.sourcePage),
    });

    return { success: true };
  } catch (err) {
    console.error('[Leads] Unexpected error:', err);
    return { success: false, error: 'An unexpected error occurred' };
  }
};

// Direct notification send for forms that handle their own DB insertion
export const sendLeadNotification = async (data: NotificationData): Promise<void> => {
  await sendNotification({
    ...data,
    region: data.region || detectRegion(data.sourcePage),
  });
};
